import { useMemo } from 'react'
import { initialUrlAuthParams } from '../lib/supabase'
import { isMobileDevice } from '../lib/device'
import styles from './Confirmed.module.css'

// Must match AuthRepository.APP_AUTH_DEEPLINK on mobile.
const APP_DEEPLINK = 'com.bumpkinlabs.recall://login-callback'
const PLAY_URL = 'https://play.google.com/store/apps/details?id=com.bumpkinlabs.recall'
const APP_STORE_URL = 'https://apps.apple.com/app/id6766313661'

type LinkKind = 'signup' | 'recovery' | 'error'

// Mobile needs a tap on Open Recall: in-app email browsers block the auto-redirect to the custom scheme.
// Never exchange a PKCE ?code= here: the code_verifier lives on the phone that started the flow.
export default function Confirmed() {
  const { kind, appDeepLink } = useMemo(() => {
    // Use the module-load snapshot, not useLocation(): supabase has already stripped the live URL.
    const search = initialUrlAuthParams.search ?? ''
    const hash = initialUrlAuthParams.hash ?? ''
    // Fragment for implicit flow, query for PKCE and errors.
    const fromHash = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : '')
    const fromQuery = new URLSearchParams(search.startsWith('?') ? search.slice(1) : '')
    const get = (key: string) => fromHash.get(key) ?? fromQuery.get(key)

    const hasError = get('error') !== null || get('error_code') !== null
    const type = get('type')

    let resolved: LinkKind = 'signup'
    if (hasError) resolved = 'error'
    else if (type === 'recovery') resolved = 'recovery'

    // Params forwarded untouched: the app's handleDeeplinks() needs them.
    return { kind: resolved, appDeepLink: `${APP_DEEPLINK}${search}${hash}` }
  }, [])

  // The custom-scheme deep link is dead on a computer, so desktop gets browser paths instead.
  const isMobile = isMobileDevice(navigator.userAgent, navigator.maxTouchPoints)

  if (kind === 'error') {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <p className={styles.emoji}>⏳</p>
          <h1 className={styles.title}>This link has expired or was already used</h1>
          <p className={styles.body}>
            Email links are single-use and expire after a while. Open Recall to request a fresh one,
            or sign in if your account is already confirmed.
          </p>
          <a className={styles.primary} href="/login">Sign in</a>
        </div>
      </div>
    )
  }

  if (kind === 'recovery') {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <p className={styles.emoji}>🔑</p>
          <h1 className={styles.title}>Reset your password</h1>
          <p className={styles.body}>
            {isMobile
              ? 'Open Recall on your phone to set a new password. On this device, you can set it in your browser instead.'
              : 'Set a new password here in your browser, then sign in on your phone with it.'}
          </p>
          {isMobile ? (
            <a className={styles.primary} href={appDeepLink}>Open Recall</a>
          ) : (
            <a className={styles.primary} href="/reset-password">Set a new password</a>
          )}
          <div className={styles.stores}>
            <a className={styles.storeLink} href={PLAY_URL}>Google Play</a>
            <a className={styles.storeLink} href={APP_STORE_URL}>App Store</a>
          </div>
          {isMobile && (
            <a className={styles.fallback} href="/reset-password">Set a new password in your browser →</a>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <p className={styles.emoji}>✓</p>
        <h1 className={styles.title}>Your email is verified</h1>
        <p className={styles.body}>
          {isMobile
            ? 'You are all set. Open Recall on your phone to finish signing in.'
            : 'You are all set. Sign in to Recall on your phone with your email and password, or continue here in your browser.'}
        </p>
        {/* Via /login, not /today: Login redirects with the post-auth marker, so a new account
            lands on the book picker (and a browser without the session gets the sign-in form). */}
        {isMobile ? (
          <a className={styles.primary} href={appDeepLink}>Open Recall</a>
        ) : (
          <a className={styles.primary} href="/login">Continue in your browser</a>
        )}
        <div className={styles.stores}>
          <a className={styles.storeLink} href={PLAY_URL}>Get it on Google Play</a>
          <a className={styles.storeLink} href={APP_STORE_URL}>Download on the App Store</a>
        </div>
        {isMobile && <a className={styles.fallback} href="/login">Continue in your browser →</a>}
      </div>
    </div>
  )
}
